import React, { useState, useRef, useEffect } from 'react';
import './styles.css';
import { CalendarIcon } from '../Icons/CalendarIcon';
var DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
var MONTHS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
];
export default function CalendarInput(_a) {
    var name = _a.name, _b = _a.value, value = _b === void 0 ? '' : _b, _c = _a.variant, variant = _c === void 0 ? 'outlined' : _c, _d = _a.size, size = _d === void 0 ? 'medium' : _d, _e = _a.disabled, disabled = _e === void 0 ? false : _e, _f = _a.error, error = _f === void 0 ? false : _f, _g = _a.required, required = _g === void 0 ? false : _g, min = _a.min, max = _a.max, onChange = _a.onChange, _h = _a.className, className = _h === void 0 ? '' : _h;
    var _j = useState(false), isOpen = _j[0], setIsOpen = _j[1];
    var _k = useState(function () { return (value ? new Date("".concat(value, "T00:00:00")) : new Date()); }), viewDate = _k[0], setViewDate = _k[1];
    var _l = useState({ top: 0, left: 0, maxHeight: 300 }), coords = _l[0], setCoords = _l[1];
    var ref = useRef(null);
    var selectedDate = value ? new Date("".concat(value, "T00:00:00")) : null;
    var baseClasses = [
        'calendar-input',
        "calendar-input--".concat(variant),
        "calendar-input--".concat(size),
        disabled ? 'calendar-input--disabled' : '',
        error ? 'calendar-input--error' : '',
        className,
    ].filter(Boolean).join(' ');
    // Close on click outside
    useEffect(function () {
        var handleClickOutside = function (e) {
            if (ref.current && !ref.current.contains(e.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('click', handleClickOutside);
        return function () { return document.removeEventListener('click', handleClickOutside); };
    }, []);
    // Calculate position
    var calculateCoords = function () {
        if (!ref.current)
            return;
        var rect = ref.current.getBoundingClientRect();
        var viewportHeight = window.innerHeight;
        var spaceBelow = viewportHeight - rect.bottom;
        var padding = 8;
        var maxHeight = Math.max(spaceBelow - padding, 200);
        setCoords({
            top: rect.bottom + 4,
            left: rect.left,
            maxHeight: maxHeight,
        });
    };
    useEffect(function () {
        var cleanup = function () { };
        if (isOpen) {
            calculateCoords();
            window.addEventListener('scroll', calculateCoords, true);
            window.addEventListener('resize', calculateCoords);
            cleanup = function () {
                window.removeEventListener('scroll', calculateCoords, true);
                window.removeEventListener('resize', calculateCoords);
            };
        }
        return cleanup;
    }, [isOpen]);
    var handleOpen = function () {
        if (!disabled) {
            calculateCoords();
            setIsOpen(!isOpen);
        }
    };
    var getDaysInMonth = function (year, month) { return new Date(year, month + 1, 0).getDate(); };
    var getFirstDayOfMonth = function (year, month) { return new Date(year, month, 1).getDay(); };
    var handlePrevMonth = function (e) {
        e.stopPropagation();
        setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1));
    };
    var handleNextMonth = function (e) {
        e.stopPropagation();
        setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1));
    };
    var handleDateSelect = function (day) {
        var year = viewDate.getFullYear();
        var month = String(viewDate.getMonth() + 1).padStart(2, '0');
        var dayStr = String(day).padStart(2, '0');
        var dateValue = "".concat(year, "-").concat(month, "-").concat(dayStr);
        // Check min/max constraints
        if (min && dateValue < min)
            return;
        if (max && dateValue > max)
            return;
        // Create a synthetic event
        var syntheticEvent = {
            target: { name: name, value: dateValue },
            currentTarget: { name: name, value: dateValue },
        };
        onChange === null || onChange === void 0 ? void 0 : onChange(syntheticEvent);
        setIsOpen(false);
    };
    var isDateDisabled = function (day) {
        var year = viewDate.getFullYear();
        var month = String(viewDate.getMonth() + 1).padStart(2, '0');
        var dayStr = String(day).padStart(2, '0');
        var dateValue = "".concat(year, "-").concat(month, "-").concat(dayStr);
        if (min && dateValue < min)
            return true;
        if (max && dateValue > max)
            return true;
        return false;
    };
    var isToday = function (day) {
        var today = new Date();
        return (day === today.getDate()
            && viewDate.getMonth() === today.getMonth()
            && viewDate.getFullYear() === today.getFullYear());
    };
    var isSelected = function (day) {
        if (!selectedDate)
            return false;
        return (day === selectedDate.getDate()
            && viewDate.getMonth() === selectedDate.getMonth()
            && viewDate.getFullYear() === selectedDate.getFullYear());
    };
    var formatDisplayDate = function (dateStr) {
        if (!dateStr)
            return 'Select date';
        var date = new Date("".concat(dateStr, "T00:00:00"));
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    };
    var renderCalendar = function () {
        var year = viewDate.getFullYear();
        var month = viewDate.getMonth();
        var daysInMonth = getDaysInMonth(year, month);
        var firstDay = getFirstDayOfMonth(year, month);
        var days = [];
        // Empty cells for days before the first day of the month
        for (var i = 0; i < firstDay; i++) {
            days.push(React.createElement("div", { key: "empty-".concat(i), className: "calendar-day empty" }));
        }
        var _loop_1 = function (day) {
            var dayDisabled = isDateDisabled(day);
            var dayClasses = [
                'calendar-day',
                isToday(day) ? 'today' : '',
                isSelected(day) ? 'selected' : '',
                dayDisabled ? 'disabled' : '',
            ].filter(Boolean).join(' ');
            days.push(React.createElement("div", { key: day, className: dayClasses, onClick: function () { return !dayDisabled && handleDateSelect(day); } }, day));
        };
        // Days of the month
        for (var day = 1; day <= daysInMonth; day++) {
            _loop_1(day);
        }
        return days;
    };
    return (React.createElement("div", { className: "calendar-wrapper", ref: ref },
        React.createElement("div", { id: name, className: "".concat(baseClasses, " calendar-trigger"), onClick: handleOpen },
            React.createElement("span", { className: "calendar-value" }, formatDisplayDate(value)),
            React.createElement("span", { style: { display: 'flex', alignItems: 'center', height: '14px' } },
                React.createElement(CalendarIcon, { size: "small" }))),
        isOpen && (React.createElement("div", { className: "calendar-dropdown", style: {
                position: 'fixed',
                top: coords.top,
                left: coords.left,
                maxHeight: coords.maxHeight,
                zIndex: 9999,
            } },
            React.createElement("div", { className: "calendar-header" },
                React.createElement("button", { type: "button", className: "calendar-nav", onClick: handlePrevMonth }, "\u2039"),
                React.createElement("span", { className: "calendar-month-year" },
                    MONTHS[viewDate.getMonth()],
                    ' ',
                    viewDate.getFullYear()),
                React.createElement("button", { type: "button", className: "calendar-nav", onClick: handleNextMonth }, "\u203A")),
            React.createElement("div", { className: "calendar-weekdays" }, DAYS.map(function (day) { return (React.createElement("div", { key: day, className: "calendar-weekday" }, day)); })),
            React.createElement("div", { className: "calendar-days" }, renderCalendar()))),
        React.createElement("input", { type: "hidden", name: name, value: value, required: required })));
}
//# sourceMappingURL=Input.js.map